// src/api/complianceApi.ts
//
// RTK Query endpoints for the CRM Compliance domain.
// Backend contract: server/src/routes/compliance.ts.
//
// Tag strategy — one type per resource + LIST + per-subject keys so
// the realtime hook can invalidate only the surfaces that care.
//
//   ComplianceCategory/LIST
//   ComplianceCategory/{id}
//   ComplianceRequirement/LIST
//   ComplianceRequirement/{id}
//   ComplianceAssignment/LIST
//   ComplianceAssignment/{id}
//   ComplianceAssignment/subject-{subjectType}-{subjectId}
//   ComplianceDocument/LIST
//   ComplianceDocument/{id}
//   ComplianceDocument/subject-{subjectType}-{subjectId}
//   ComplianceApproval/doc-{documentId}
//   ComplianceReview/LIST
//   ComplianceEligibility/{subjectType}-{subjectId}
//   ComplianceCoverage/SUMMARY
//   ComplianceCoverage/subject-{subjectType}
//   ComplianceCoverage/REQUIREMENTS

import { baseApi } from './baseApi';
import type {
  ComplianceApproval,
  ComplianceCategory,
  ComplianceDocument,
  ComplianceRequirement,
  ComplianceRequirementAssignment,
  ComplianceReview,
  ComplianceReviewOutcome,
  ComplianceSubjectType,
  CoverageForSubjectRow,
  EligibilityResult,
  RequirementCoverageRow,
  WorkspaceCoverageSummary,
  ComplianceEnforcementMode,
  ComplianceDocumentStatus,
} from '../types/api';

const LIST_ID = 'LIST' as const;

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

export interface CreateComplianceCategoryRequest {
  key: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
}
export interface UpdateComplianceCategoryRequest {
  name?: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  isActive?: boolean;
}

export interface CreateComplianceRequirementRequest {
  categoryId: string;
  name: string;
  description?: string | null;
  requirementType?: string | null;
  enforcementMode?: ComplianceEnforcementMode;
  validityDays?: number | null;
  renewalLeadDays?: number | null;
  approvalRequired?: boolean;
  reviewRequired?: boolean;
  isMandatory?: boolean;
}
export interface UpdateComplianceRequirementRequest {
  categoryId?: string;
  name?: string;
  description?: string | null;
  requirementType?: string | null;
  enforcementMode?: ComplianceEnforcementMode;
  validityDays?: number | null;
  renewalLeadDays?: number | null;
  approvalRequired?: boolean;
  reviewRequired?: boolean;
  isMandatory?: boolean;
  isActive?: boolean;
}

export interface CreateComplianceAssignmentRequest {
  requirementId: string;
  subjectType: ComplianceSubjectType;
  subjectId: string;
  effectiveFrom?: string;
  effectiveUntil?: string | null;
  isRequired?: boolean;
}
export interface UpdateComplianceAssignmentRequest {
  effectiveFrom?: string;
  effectiveUntil?: string | null;
  isRequired?: boolean;
}

export interface CreateComplianceDocumentRequest {
  requirementId: string;
  requirementAssignmentId?: string | null;
  subjectType: ComplianceSubjectType;
  subjectId: string;
  ownerUserId?: string | null;
  title: string;
  issuedAt?: string | null;
  effectiveAt?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
  notes?: string | null;
}
export interface UpdateComplianceDocumentRequest {
  title?: string;
  ownerUserId?: string | null;
  issuedAt?: string | null;
  effectiveAt?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
  notes?: string | null;
}

export interface ListComplianceRequirementsParams {
  categoryId?: string;
  enforcementMode?: ComplianceEnforcementMode;
  activeOnly?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ListComplianceAssignmentsParams {
  requirementId?: string;
  subjectType?: ComplianceSubjectType;
  subjectId?: string;
  activeOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListComplianceDocumentsParams {
  requirementId?: string;
  requirementAssignmentId?: string;
  subjectType?: ComplianceSubjectType;
  subjectId?: string;
  status?: ComplianceDocumentStatus | ComplianceDocumentStatus[];
  ownerUserId?: string;
  expiringWithinDays?: number;
  limit?: number;
  offset?: number;
}

export interface RequestApprovalBody {
  approverUserId?: string | null;
  reason?: string | null;
}

export interface DecideApprovalBody {
  reason?: string | null;
}

export interface DelegateApprovalBody {
  delegateeUserId: string;
  reason?: string | null;
}

export interface ScheduleReviewRequest {
  documentId: string;
  reviewerUserId?: string | null;
  scheduledFor: string;
  notes?: string | null;
}

export interface CompleteReviewRequest {
  outcome: ComplianceReviewOutcome;
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// Tag helpers
// ---------------------------------------------------------------------------

function assignmentTagsFor(row: ComplianceRequirementAssignment) {
  return [
    { type: 'ComplianceAssignment' as const, id: row.id },
    { type: 'ComplianceAssignment' as const, id: LIST_ID },
    { type: 'ComplianceAssignment' as const, id: `subject-${row.subjectType}-${row.subjectId}` },
    { type: 'ComplianceEligibility' as const, id: `${row.subjectType}-${row.subjectId}` },
    { type: 'ComplianceCoverage' as const, id: 'SUMMARY' },
    { type: 'ComplianceCoverage' as const, id: `subject-${row.subjectType}` },
    { type: 'ComplianceCoverage' as const, id: 'REQUIREMENTS' },
  ];
}

function documentTagsFor(row: ComplianceDocument) {
  return [
    { type: 'ComplianceDocument' as const, id: row.id },
    { type: 'ComplianceDocument' as const, id: LIST_ID },
    { type: 'ComplianceDocument' as const, id: `subject-${row.subjectType}-${row.subjectId}` },
    { type: 'ComplianceApproval' as const, id: `doc-${row.id}` },
    { type: 'ComplianceEligibility' as const, id: `${row.subjectType}-${row.subjectId}` },
    { type: 'ComplianceCoverage' as const, id: 'SUMMARY' },
    { type: 'ComplianceCoverage' as const, id: `subject-${row.subjectType}` },
    { type: 'ComplianceCoverage' as const, id: 'REQUIREMENTS' },
  ];
}

interface PagedResponse<T> {
  rows: T[];
  total: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const complianceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Categories ----------------------------------------------------
    getComplianceCategories: builder.query<
      ComplianceCategory[],
      { includeInactive?: boolean } | void
    >({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.includeInactive) s.append('includeInactive', '1');
        const qs = s.toString();
        return `/compliance/categories${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) => {
        const list: Array<{ type: 'ComplianceCategory'; id: string }> = [
          { type: 'ComplianceCategory', id: LIST_ID },
        ];
        (result ?? []).forEach((r) => list.push({ type: 'ComplianceCategory', id: r.id }));
        return list;
      },
    }),

    createComplianceCategory: builder.mutation<ComplianceCategory, CreateComplianceCategoryRequest>({
      query: (body) => ({ url: '/compliance/categories', method: 'POST', body }),
      invalidatesTags: [{ type: 'ComplianceCategory', id: LIST_ID }],
    }),

    updateComplianceCategory: builder.mutation<
      ComplianceCategory,
      { id: string; patch: UpdateComplianceCategoryRequest }
    >({
      query: ({ id, patch }) => ({ url: `/compliance/categories/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ComplianceCategory', id: arg.id },
        { type: 'ComplianceCategory', id: LIST_ID },
      ],
    }),

    deleteComplianceCategory: builder.mutation<void, string>({
      query: (id) => ({ url: `/compliance/categories/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ComplianceCategory', id },
        { type: 'ComplianceCategory', id: LIST_ID },
      ],
    }),

    // ---- Requirements --------------------------------------------------
    getComplianceRequirements: builder.query<
      PagedResponse<ComplianceRequirement>,
      ListComplianceRequirementsParams | void
    >({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.categoryId) s.append('categoryId', params.categoryId);
        if (params?.enforcementMode) s.append('enforcementMode', params.enforcementMode);
        if (params?.activeOnly) s.append('activeOnly', '1');
        if (params?.q) s.append('q', params.q);
        if (params?.limit) s.append('limit', String(params.limit));
        if (params?.offset) s.append('offset', String(params.offset));
        const qs = s.toString();
        return `/compliance/requirements${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) => {
        const list: Array<{ type: 'ComplianceRequirement'; id: string }> = [
          { type: 'ComplianceRequirement', id: LIST_ID },
        ];
        (result?.rows ?? []).forEach((r) => list.push({ type: 'ComplianceRequirement', id: r.id }));
        return list;
      },
    }),

    getComplianceRequirement: builder.query<ComplianceRequirement, string>({
      query: (id) => `/compliance/requirements/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ComplianceRequirement', id }],
    }),

    createComplianceRequirement: builder.mutation<
      ComplianceRequirement,
      CreateComplianceRequirementRequest
    >({
      query: (body) => ({ url: '/compliance/requirements', method: 'POST', body }),
      invalidatesTags: [
        { type: 'ComplianceRequirement', id: LIST_ID },
        { type: 'ComplianceCoverage', id: 'REQUIREMENTS' },
      ],
    }),

    updateComplianceRequirement: builder.mutation<
      ComplianceRequirement,
      { id: string; patch: UpdateComplianceRequirementRequest }
    >({
      query: ({ id, patch }) => ({ url: `/compliance/requirements/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ComplianceRequirement', id: arg.id },
        { type: 'ComplianceRequirement', id: LIST_ID },
        { type: 'ComplianceCoverage', id: 'REQUIREMENTS' },
      ],
    }),

    deleteComplianceRequirement: builder.mutation<void, string>({
      query: (id) => ({ url: `/compliance/requirements/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ComplianceRequirement', id },
        { type: 'ComplianceRequirement', id: LIST_ID },
        { type: 'ComplianceCoverage', id: 'REQUIREMENTS' },
      ],
    }),

    // ---- Assignments ---------------------------------------------------
    getComplianceAssignments: builder.query<
      PagedResponse<ComplianceRequirementAssignment>,
      ListComplianceAssignmentsParams | void
    >({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.requirementId) s.append('requirementId', params.requirementId);
        if (params?.subjectType) s.append('subjectType', params.subjectType);
        if (params?.subjectId) s.append('subjectId', params.subjectId);
        if (params?.activeOnly) s.append('activeOnly', '1');
        if (params?.limit) s.append('limit', String(params.limit));
        if (params?.offset) s.append('offset', String(params.offset));
        const qs = s.toString();
        return `/compliance/assignments${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result, _e, arg) => {
        const list: Array<{ type: 'ComplianceAssignment'; id: string }> = [
          { type: 'ComplianceAssignment', id: LIST_ID },
        ];
        (result?.rows ?? []).forEach((r) => {
          list.push({ type: 'ComplianceAssignment', id: r.id });
          list.push({
            type: 'ComplianceAssignment',
            id: `subject-${r.subjectType}-${r.subjectId}`,
          });
        });
        if (arg?.subjectType && arg?.subjectId) {
          list.push({
            type: 'ComplianceAssignment',
            id: `subject-${arg.subjectType}-${arg.subjectId}`,
          });
        }
        return list;
      },
    }),

    createComplianceAssignment: builder.mutation<
      ComplianceRequirementAssignment,
      CreateComplianceAssignmentRequest
    >({
      query: (body) => ({ url: '/compliance/assignments', method: 'POST', body }),
      invalidatesTags: (result) => (result ? assignmentTagsFor(result) : []),
    }),

    updateComplianceAssignment: builder.mutation<
      ComplianceRequirementAssignment,
      { id: string; patch: UpdateComplianceAssignmentRequest }
    >({
      query: ({ id, patch }) => ({ url: `/compliance/assignments/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (result) => (result ? assignmentTagsFor(result) : []),
    }),

    endComplianceAssignment: builder.mutation<ComplianceRequirementAssignment, string>({
      query: (id) => ({ url: `/compliance/assignments/${id}/end`, method: 'POST' }),
      invalidatesTags: (result) => (result ? assignmentTagsFor(result) : []),
    }),

    deleteComplianceAssignment: builder.mutation<void, string>({
      query: (id) => ({ url: `/compliance/assignments/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ComplianceAssignment', id },
        { type: 'ComplianceAssignment', id: LIST_ID },
        { type: 'ComplianceCoverage', id: 'SUMMARY' },
      ],
    }),

    // ---- Documents -----------------------------------------------------
    getComplianceDocuments: builder.query<
      PagedResponse<ComplianceDocument>,
      ListComplianceDocumentsParams | void
    >({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.requirementId) s.append('requirementId', params.requirementId);
        if (params?.requirementAssignmentId) s.append('requirementAssignmentId', params.requirementAssignmentId);
        if (params?.subjectType) s.append('subjectType', params.subjectType);
        if (params?.subjectId) s.append('subjectId', params.subjectId);
        if (params?.status) {
          if (Array.isArray(params.status)) params.status.forEach((v) => s.append('status', v));
          else s.append('status', params.status);
        }
        if (params?.ownerUserId) s.append('ownerUserId', params.ownerUserId);
        if (params?.expiringWithinDays !== undefined) s.append('expiringWithinDays', String(params.expiringWithinDays));
        if (params?.limit) s.append('limit', String(params.limit));
        if (params?.offset !== undefined) s.append('offset', String(params.offset));
        const qs = s.toString();
        return `/compliance/documents${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result, _e, arg) => {
        const list: Array<{ type: 'ComplianceDocument'; id: string }> = [
          { type: 'ComplianceDocument', id: LIST_ID },
        ];
        (result?.rows ?? []).forEach((r) => {
          list.push({ type: 'ComplianceDocument', id: r.id });
          list.push({
            type: 'ComplianceDocument',
            id: `subject-${r.subjectType}-${r.subjectId}`,
          });
        });
        if (arg?.subjectType && arg?.subjectId) {
          list.push({
            type: 'ComplianceDocument',
            id: `subject-${arg.subjectType}-${arg.subjectId}`,
          });
        }
        return list;
      },
    }),

    getComplianceDocument: builder.query<ComplianceDocument, string>({
      query: (id) => `/compliance/documents/${id}`,
      providesTags: (_r, _e, id) => [
        { type: 'ComplianceDocument', id },
        { type: 'ComplianceApproval', id: `doc-${id}` },
      ],
    }),

    createComplianceDocument: builder.mutation<ComplianceDocument, CreateComplianceDocumentRequest>({
      query: (body) => ({ url: '/compliance/documents', method: 'POST', body }),
      invalidatesTags: (result) => (result ? documentTagsFor(result) : []),
    }),

    updateComplianceDocument: builder.mutation<
      ComplianceDocument,
      { id: string; patch: UpdateComplianceDocumentRequest }
    >({
      query: ({ id, patch }) => ({ url: `/compliance/documents/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (result) => (result ? documentTagsFor(result) : []),
    }),

    submitComplianceDocument: builder.mutation<ComplianceDocument, string>({
      query: (id) => ({ url: `/compliance/documents/${id}/submit`, method: 'POST' }),
      invalidatesTags: (result) => (result ? documentTagsFor(result) : []),
    }),

    archiveComplianceDocument: builder.mutation<ComplianceDocument, string>({
      query: (id) => ({ url: `/compliance/documents/${id}/archive`, method: 'POST' }),
      invalidatesTags: (result) => (result ? documentTagsFor(result) : []),
    }),

    renewComplianceDocument: builder.mutation<
      { predecessor: ComplianceDocument; successor: ComplianceDocument },
      { id: string; effectiveAt?: string | null; expiresAt?: string | null; notes?: string | null }
    >({
      query: ({ id, ...body }) => ({ url: `/compliance/documents/${id}/renew`, method: 'POST', body }),
      invalidatesTags: (result) =>
        result
          ? [...documentTagsFor(result.predecessor), ...documentTagsFor(result.successor)]
          : [],
    }),

    /**
     * Upload endpoint. Backend returns 503 with
     * `code: 'central_documents_unavailable'` until the platform ships.
     * The UI must NOT silently no-op — it should surface the error copy.
     */
    uploadComplianceDocument: builder.mutation<ComplianceDocument, { id: string; file: File }>({
      query: ({ id, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `/compliance/documents/${id}/upload`, method: 'POST', body: form };
      },
      invalidatesTags: (result) => (result ? documentTagsFor(result) : []),
    }),

    // ---- Approvals -----------------------------------------------------
    listApprovalsForDocument: builder.query<ComplianceApproval[], string>({
      query: (documentId) => `/compliance/documents/${documentId}/approvals`,
      providesTags: (_r, _e, documentId) => [
        { type: 'ComplianceApproval', id: `doc-${documentId}` },
      ],
    }),

    requestApproval: builder.mutation<
      { approval: ComplianceApproval; document: ComplianceDocument },
      { documentId: string; body: RequestApprovalBody }
    >({
      query: ({ documentId, body }) => ({
        url: `/compliance/documents/${documentId}/approvals`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result ? [...documentTagsFor(result.document)] : [],
    }),

    approveApproval: builder.mutation<
      { approval: ComplianceApproval; document: ComplianceDocument },
      { id: string; body: DecideApprovalBody }
    >({
      query: ({ id, body }) => ({ url: `/compliance/approvals/${id}/approve`, method: 'POST', body }),
      invalidatesTags: (result) => (result ? documentTagsFor(result.document) : []),
    }),

    rejectApproval: builder.mutation<
      { approval: ComplianceApproval; document: ComplianceDocument },
      { id: string; body: DecideApprovalBody }
    >({
      query: ({ id, body }) => ({ url: `/compliance/approvals/${id}/reject`, method: 'POST', body }),
      invalidatesTags: (result) => (result ? documentTagsFor(result.document) : []),
    }),

    delegateApproval: builder.mutation<
      ComplianceApproval,
      { id: string; body: DelegateApprovalBody }
    >({
      query: ({ id, body }) => ({ url: `/compliance/approvals/${id}/delegate`, method: 'POST', body }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'ComplianceApproval', id: `doc-${result.complianceDocumentId}` }]
          : [],
    }),

    cancelApproval: builder.mutation<
      ComplianceApproval,
      { id: string; body: DecideApprovalBody }
    >({
      query: ({ id, body }) => ({ url: `/compliance/approvals/${id}/cancel`, method: 'POST', body }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'ComplianceApproval', id: `doc-${result.complianceDocumentId}` }]
          : [],
    }),

    // ---- Reviews -------------------------------------------------------
    getComplianceReviews: builder.query<
      PagedResponse<ComplianceReview>,
      { documentId?: string; reviewerUserId?: string; completed?: boolean; dueBefore?: string; limit?: number; offset?: number } | void
    >({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.documentId) s.append('documentId', params.documentId);
        if (params?.reviewerUserId) s.append('reviewerUserId', params.reviewerUserId);
        if (params?.completed === true) s.append('completed', '1');
        if (params?.completed === false) s.append('completed', '0');
        if (params?.dueBefore) s.append('dueBefore', params.dueBefore);
        if (params?.limit) s.append('limit', String(params.limit));
        if (params?.offset !== undefined) s.append('offset', String(params.offset));
        const qs = s.toString();
        return `/compliance/reviews${qs ? `?${qs}` : ''}`;
      },
      providesTags: [{ type: 'ComplianceReview', id: LIST_ID }],
    }),

    scheduleComplianceReview: builder.mutation<ComplianceReview, ScheduleReviewRequest>({
      query: (body) => ({ url: '/compliance/reviews', method: 'POST', body }),
      invalidatesTags: [{ type: 'ComplianceReview', id: LIST_ID }],
    }),

    completeComplianceReview: builder.mutation<
      ComplianceReview,
      { id: string; body: CompleteReviewRequest }
    >({
      query: ({ id, body }) => ({ url: `/compliance/reviews/${id}/complete`, method: 'POST', body }),
      invalidatesTags: [{ type: 'ComplianceReview', id: LIST_ID }],
    }),

    // ---- Eligibility ---------------------------------------------------
    evaluateComplianceSubject: builder.query<
      EligibilityResult,
      { subjectType: ComplianceSubjectType; subjectId: string }
    >({
      query: ({ subjectType, subjectId }) => {
        const s = new URLSearchParams();
        s.append('subjectType', subjectType);
        s.append('subjectId', subjectId);
        return `/compliance/eligibility?${s.toString()}`;
      },
      providesTags: (_r, _e, arg) => [
        { type: 'ComplianceEligibility', id: `${arg.subjectType}-${arg.subjectId}` },
      ],
    }),

    // ---- Coverage ------------------------------------------------------
    getComplianceCoverageSummary: builder.query<WorkspaceCoverageSummary, void>({
      query: () => '/compliance/coverage/summary',
      providesTags: [{ type: 'ComplianceCoverage', id: 'SUMMARY' }],
    }),

    getComplianceCoverageForSubjectType: builder.query<
      PagedResponse<CoverageForSubjectRow>,
      { subjectType: ComplianceSubjectType; limit?: number; offset?: number }
    >({
      query: ({ subjectType, limit, offset }) => {
        const s = new URLSearchParams();
        if (limit) s.append('limit', String(limit));
        if (offset !== undefined) s.append('offset', String(offset));
        const qs = s.toString();
        return `/compliance/coverage/subjects/${subjectType}${qs ? `?${qs}` : ''}`;
      },
      providesTags: (_r, _e, arg) => [
        { type: 'ComplianceCoverage', id: `subject-${arg.subjectType}` },
      ],
    }),

    getComplianceCoverageByRequirement: builder.query<RequirementCoverageRow[], void>({
      query: () => '/compliance/coverage/requirements',
      providesTags: [{ type: 'ComplianceCoverage', id: 'REQUIREMENTS' }],
    }),
  }),
});

export const {
  // Categories
  useGetComplianceCategoriesQuery,
  useCreateComplianceCategoryMutation,
  useUpdateComplianceCategoryMutation,
  useDeleteComplianceCategoryMutation,
  // Requirements
  useGetComplianceRequirementsQuery,
  useGetComplianceRequirementQuery,
  useCreateComplianceRequirementMutation,
  useUpdateComplianceRequirementMutation,
  useDeleteComplianceRequirementMutation,
  // Assignments
  useGetComplianceAssignmentsQuery,
  useCreateComplianceAssignmentMutation,
  useUpdateComplianceAssignmentMutation,
  useEndComplianceAssignmentMutation,
  useDeleteComplianceAssignmentMutation,
  // Documents
  useGetComplianceDocumentsQuery,
  useGetComplianceDocumentQuery,
  useCreateComplianceDocumentMutation,
  useUpdateComplianceDocumentMutation,
  useSubmitComplianceDocumentMutation,
  useArchiveComplianceDocumentMutation,
  useRenewComplianceDocumentMutation,
  useUploadComplianceDocumentMutation,
  // Approvals
  useListApprovalsForDocumentQuery,
  useRequestApprovalMutation,
  useApproveApprovalMutation,
  useRejectApprovalMutation,
  useDelegateApprovalMutation,
  useCancelApprovalMutation,
  // Reviews
  useGetComplianceReviewsQuery,
  useScheduleComplianceReviewMutation,
  useCompleteComplianceReviewMutation,
  // Eligibility
  useEvaluateComplianceSubjectQuery,
  // Coverage
  useGetComplianceCoverageSummaryQuery,
  useGetComplianceCoverageForSubjectTypeQuery,
  useGetComplianceCoverageByRequirementQuery,
} = complianceApi;
